import {
  menu,
  popFilterableSimpleMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { AddCursorIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { signal } from '@preact/signals-core';
import { css, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { html } from 'lit/static-html.js';

import { DATA_SOURCE_KANBAN_GROUP_CARD_RENDER_LIMIT } from '../../../core/data-source/window.js';
import { GroupTitle } from '../../../core/group-by/group-title.js';
import type { Group } from '../../../core/group-by/trait.js';
import { dataViewI18nT } from '../../../core/i18n.js';
import type { SelectTag } from '../../../core/logical/index.js';
import { dragHandler } from '../../../core/utils/wc-dnd/dnd-context.js';
import { resolveKanbanVisibleCards } from '../card-render-window.js';
import { KANBAN_COLUMN_DEFAULT_WIDTH } from '../kanban-view-manager.js';
import type { KanbanViewUILogic } from './kanban-view-ui-logic.js';
import { KANBAN_GROUP_HEADER_HEIGHT } from './layout.js';

const styles = css`
  affine-data-view-kanban-group {
    --kanban-column-resize-hit-area: 8px;
    position: relative;
    width: var(--kanban-column-width, 260px);
    flex-shrink: 0;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    /* --kanban-group-tint is set inline from the group's SelectTag
       color. Since the palette was raised to saturated Tailwind-600
       hues (see STRONG_CHIP_LABEL_CSS in custom-theme), a 45% wash
       now reads as aggressive block-colour instead of subtle lane
       tint — the mix had to drop once the base hue got stronger.
       18% lands in the Linear / Asana range: the bucket identity
       is clear but the column never competes with card borders or
       status pills. Columns whose group has no color (No xxx,
       boolean, date ranges) fall back to transparent so the
       kanban background shows through. */
    background: color-mix(
      in srgb,
      var(--kanban-group-tint, transparent) 18%,
      transparent
    );
  }

  .group-header {
    position: sticky;
    top: 0;
    z-index: 3;
    box-sizing: border-box;
    height: ${KANBAN_GROUP_HEADER_HEIGHT}px;
    padding: 7px 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    overflow: clip;
    font-family: var(--streamlain-font-family);
    font-size: var(--data-view-cell-text-size);
    font-weight: 500;
    line-height: var(--data-view-cell-text-line-height);
    color: var(--streamlain-v2-text-primary);
    background: var(
      --streamlain-kanban-group-header-background,
      var(--streamlain-v2-layer-background-secondary)
    );
    border-radius: 8px 8px 0 0;
    box-shadow: 0 1px 0 var(--streamlain-v2-layer-insideBorder-border);
  }

  .group-header[data-sort-disabled='true'] {
    cursor: default;
  }

  affine-data-view-kanban-group[data-header-only='true'] .group-header {
    border-radius: 8px;
  }

  .kanban-view[data-kanban-y-scrolled='true']
    affine-data-view-kanban-group[data-header-only='true']
    .group-header {
    border-radius: 8px 8px 0 0;
  }

  .group-resize-handle {
    position: absolute;
    top: 0;
    right: calc(var(--kanban-column-resize-hit-area) / -2);
    bottom: 0;
    width: var(--kanban-column-resize-hit-area);
    cursor: col-resize;
    opacity: 0;
    z-index: 5;
    touch-action: none;
    transition: opacity 150ms ease 100ms;
  }

  .group-resize-handle:hover,
  .group-resize-handle:focus-visible,
  .group-resize-handle[data-resizing='true'] {
    opacity: 1;
  }

  .group-resize-handle::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    border-radius: 2px;
    background: var(--streamlain-v2-brand-primary);
    transform: translateX(0.5px);
    transition: width 200ms ease-in-out;
  }

  .group-resize-handle[data-resizing='true']::after {
    width: 4px;
    border-radius: 4px;
  }

  .group-header-title {
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--data-view-cell-text-size);
    min-width: 0;
    color: var(--streamlain-v2-text-primary);
    font-family: var(--streamlain-font-family);
    font-weight: 500;
    line-height: var(--data-view-cell-text-line-height);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .group-header > div {
    overflow: hidden !important;
  }

  .group-header-icon,
  .group-header-op,
  .group-header-icon svg,
  .group-header-op svg {
    color: var(--streamlain-v2-icon-primary);
  }

  .group-header-count {
    color: var(--streamlain-v2-text-secondary);
    background: var(--streamlain-v2-layer-background-primary);
  }

  affine-data-view-kanban-group:hover .group-header-op {
    visibility: visible;
    opacity: 1;
  }

  .group-body {
    margin-top: 4px;
    display: flex;
    flex-direction: column;
    padding: 0 4px;
    gap: 12px;
  }

  .drop-card-placeholder {
    flex-shrink: 0;
    border: 1px dashed var(--streamlain-v2-brand-primary);
    border-radius: 8px;
    background: color-mix(
      in srgb,
      var(--streamlain-v2-brand-primary) 10%,
      var(--streamlain-v2-layer-background-primary)
    );
    box-sizing: border-box;
    transition: height 120ms ease-out;
  }

  .add-card {
    display: flex;
    align-items: center;
    padding: 4px;
    border-radius: 4px;
    cursor: pointer;
    font-size: var(--data-view-cell-text-size);
    line-height: var(--data-view-cell-text-line-height);
    visibility: hidden;
    opacity: 0;
    transition: opacity 150ms cubic-bezier(0.42, 0, 1, 1);
    color: var(--streamlain-v2-text-secondary);
  }

  affine-data-view-kanban-group:hover .add-card {
    visibility: visible;
    opacity: 1;
  }

  affine-data-view-kanban-group .add-card:hover {
    background-color: var(--streamlain-v2-layer-background-hoverOverlay);
    color: var(--streamlain-v2-text-primary);
  }

  .sortable-ghost {
    background-color: var(--streamlain-v2-layer-background-hoverOverlay);
    opacity: 0.5;
  }

  .sortable-drag {
    background-color: var(--streamlain-v2-layer-background-primary);
  }

  .card-overflow {
    border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
    border-radius: 6px;
    color: var(--streamlain-v2-text-secondary);
    font-size: var(--data-view-cell-text-size);
    line-height: var(--data-view-cell-text-line-height);
    padding: 8px;
    text-align: center;
    cursor: pointer;
    user-select: none;
    background: color-mix(
      in srgb,
      var(--streamlain-v2-layer-background-primary) 88%,
      transparent
    );
  }

  .card-overflow:hover {
    background: var(--streamlain-v2-layer-background-hoverOverlay);
    color: var(--streamlain-v2-text-primary);
  }
`;

export class KanbanGroup extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = styles;

  /**
   * Per-cell card render window. Starts at the policy sample size;
   * clicking the "+N more" tile pulls in one more policy step for
   * exactly this cell — other cells keep their small sample so the
   * board renders only what the user asked to see.
   */
  private readonly cardRenderLimit$ = signal(
    DATA_SOURCE_KANBAN_GROUP_CARD_RENDER_LIMIT
  );

  private readonly showMoreCards = () => {
    this.cardRenderLimit$.value += DATA_SOURCE_KANBAN_GROUP_CARD_RENDER_LIMIT;
  };

  private readonly clickAddCard = () => {
    const id = this.view.addCard('end', this.group.key, this.rowKey);
    requestAnimationFrame(() => {
      const columnId =
        this.view.mainProperties$.value.titleColumn ||
        this.view.propertyIds$.value[0];
      if (!columnId) return;
      this.kanbanViewLogic.selectionController.selection = {
        selectionType: 'cell',
        groupKey: this.group.key,
        rowKey: this.rowKey,
        cardId: id,
        columnId,
        isEditing: true,
      };
    });
    this.requestUpdate();
  };

  private readonly clickAddCardInStart = () => {
    const id = this.view.addCard('start', this.group.key, this.rowKey);
    requestAnimationFrame(() => {
      const columnId =
        this.view.mainProperties$.value.titleColumn ||
        this.view.propertyIds$.value[0];
      if (!columnId) return;
      this.kanbanViewLogic.selectionController.selection = {
        selectionType: 'cell',
        groupKey: this.group.key,
        rowKey: this.rowKey,
        cardId: id,
        columnId,
        isEditing: true,
      };
    });
    this.requestUpdate();
  };

  private readonly clickGroupOptions = (e: MouseEvent) => {
    const ele = e.currentTarget as HTMLElement;
    popFilterableSimpleMenu(popupTargetFromElement(ele), [
      menu.action({
        name: dataViewI18nT('kanban.ungroup'),
        hide: () => this.group.value == null,
        select: () => {
          this.group.rows.forEach(row => {
            this.group.manager.removeFromGroup(row.rowId, this.group.key);
          });
          this.requestUpdate();
        },
      }),
      menu.action({
        name: dataViewI18nT('kanban.deleteLabel'),
        // No label exists for the "No Column" group — nothing to
        // delete there. Only labels backed by a select option are
        // removable.
        hide: () => this.group.value == null,
        select: () => {
          // Put every card in this lane back to "no group" by
          // nulling its cell value, then drop the option from the
          // select column's `data.options`. Existing Ungroup only
          // does the first step; this action also deletes the label.
          this.group.rows.forEach(row => {
            this.group.manager.removeFromGroup(row.rowId, this.group.key);
          });
          const property = this.group.manager.property$.value;
          if (property) {
            const optionId = this.group.value;
            property.dataUpdate(oldData => {
              const options =
                (oldData as { options?: Array<{ id: string }> }).options ?? [];
              return {
                options: options.filter(o => o.id !== optionId),
              };
            });
          }
          this.requestUpdate();
        },
      }),
      menu.action({
        name: dataViewI18nT('kanban.deleteLoadedCards'),
        select: () => {
          this.view.rowsDelete(this.group.rows.map(row => row.rowId));
          this.requestUpdate();
        },
      }),
    ]);
  };

  private readonly startColumnResize = (event: PointerEvent) => {
    if (this.view.readonly$.value || event.button !== 0 || !event.isPrimary) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = this.view.columnWidth$.value;
    const handle = event.currentTarget as HTMLElement;
    let pendingWidth = startWidth;
    let frame = 0;
    handle.dataset['resizing'] = 'true';

    const commit = () => {
      frame = 0;
      this.view.setColumnWidth(pendingWidth);
    };
    const schedule = (width: number) => {
      pendingWidth = width;
      if (!frame) {
        frame = requestAnimationFrame(commit);
      }
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
      delete handle.dataset['resizing'];
      this.view.setColumnWidth(pendingWidth);
    };
    const onMove = (move: PointerEvent) => {
      if (move.pointerId !== event.pointerId) return;
      schedule(startWidth + move.clientX - startX);
    };
    const onUp = (up: PointerEvent) => {
      if (up.pointerId !== event.pointerId) return;
      cleanup();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  private readonly resetColumnWidth = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.view.setColumnWidth(KANBAN_COLUMN_DEFAULT_WIDTH);
  };

  private renderResizeHandle() {
    return this.view.readonly$.value
      ? nothing
      : html`<div
          class="group-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize Kanban columns"
          title="Drag to resize columns"
          @pointerdown="${this.startColumnResize}"
          @dblclick="${this.resetColumnWidth}"
        ></div>`;
  }

  private renderHeader(countOverride?: number) {
    const sortDisabled = this.group.manager.isExplicitSort$.value;
    const title = GroupTitle(this.group, {
      readonly: this.view.readonly$.value,
      countOverride,
      clickAdd: this.clickAddCardInStart,
      clickOps: this.clickGroupOptions,
      hideIcon: true,
    });
    if (this.headerOnly) {
      return this.view.readonly$.value
        ? html`<div class="group-header">${title}</div>`
        : html`<div
            class="group-header"
            ${dragHandler(this.group.key)}
            aria-disabled="${sortDisabled ? 'true' : 'false'}"
            data-sort-disabled="${sortDisabled ? 'true' : 'false'}"
            title="${sortDisabled
              ? dataViewI18nT('kanban.reorderRequiresDefaultSort')
              : dataViewI18nT('kanban.reorderColumnGroup')}"
          >
            ${title}
          </div>`;
    }
    if (this.view.readonly$.value) {
      return html`<div class="group-header">${title}</div>`;
    }
    return html`<div
      class="group-header"
      ${dragHandler(this.group.key)}
      aria-disabled="${sortDisabled ? 'true' : 'false'}"
      data-sort-disabled="${sortDisabled ? 'true' : 'false'}"
      title="${sortDisabled
        ? dataViewI18nT('kanban.reorderRequiresDefaultSort')
        : dataViewI18nT('kanban.reorderColumnGroup')}"
    >
      ${title}
    </div>`;
  }

  override render() {
    this.toggleAttribute('data-header-only', this.headerOnly);
    this.style.setProperty(
      '--kanban-column-width',
      `${this.view.columnWidth$.value}px`
    );
    // In 2-D mode the row host passes a rowKey; a card is only visible
    // in this cell when it belongs to BOTH the L1 group (column) AND
    // the L2 group (row). The intersection is computed here because the
    // two GroupTrait instances track rows independently — each hands
    // back the full per-key rows list, and the cell is their overlap.
    const rowMap = this.kanbanViewLogic.group2Map$.value;
    const rowRowIds = rowMap?.[this.rowKey]?.rows.map(r => r.rowId);
    const { visibleCards, hiddenCount } = resolveKanbanVisibleCards(
      this.group.rows,
      rowRowIds,
      this.cardRenderLimit$.value
    );
    // Pull the SelectTag color for this group, if the group-by is
    // (multi-)select. `group.property.data$.value.options` is the
    // typed option list; `group.value` is the option id. Unknown
    // groups (No xxx, boolean, date) return undefined — the CSS
    // fallback then resolves to `transparent`.
    const propData = this.group.property.data$.value as
      | { options?: SelectTag[] }
      | undefined;
    const tagColor = propData?.options?.find(
      o => o.id === this.group.value
    )?.color;
    if (tagColor) {
      this.style.setProperty('--kanban-group-tint', tagColor);
    } else {
      this.style.removeProperty('--kanban-group-tint');
    }
    if (this.headerOnly) {
      return html`${this.renderResizeHandle()}${this.renderHeader()}`;
    }
    const draggedIds = this.kanbanViewLogic.reorder.state$.value?.draggedIds;
    const containsDraggedCard = visibleCards.some(row =>
      draggedIds?.has(row.rowId)
    );
    // Foreign target columns need a real DOM placeholder so the column
    // offers one extra card-height of layout space. The source/current
    // column already contains the hidden dragged card, so adding a
    // second slot there would make the same-column preview too tall.
    const dropPlaceholder = containsDraggedCard
      ? undefined
      : this.kanbanViewLogic.ui$.value?.getDropPlaceholder(
          this.group.key,
          this.rowKey
        );
    const renderDropPlaceholder = () =>
      dropPlaceholder
        ? html`<div
            class="drop-card-placeholder"
            style="height: ${dropPlaceholder.height}px;"
          ></div>`
        : nothing;
    return html`
      ${this.renderResizeHandle()}
      ${this.hideHeader ? nothing : this.renderHeader(this.group.totalCount)}
      <div class="group-body">
        ${repeat(
          visibleCards,
          row => row.rowId,
          (row, index) => {
            return html`
              ${dropPlaceholder?.index === index
                ? renderDropPlaceholder()
                : nothing}
              <affine-data-view-kanban-card
                data-card-id="${row.rowId}"
                data-row-key="${this.rowKey}"
                .groupKey="${this.group.key}"
                .rowKey="${this.rowKey}"
                .kanbanViewLogic="${this.kanbanViewLogic}"
                .cardId="${row.rowId}"
              ></affine-data-view-kanban-card>
            `;
          }
        )}
        ${dropPlaceholder && dropPlaceholder.index >= visibleCards.length
          ? renderDropPlaceholder()
          : nothing}
        ${hiddenCount > 0
          ? html`<div
              class="card-overflow"
              role="button"
              title="${dataViewI18nT('kanban.showMoreCardsTitle')}"
              data-hidden-kanban-card-count="${hiddenCount}"
              @click="${this.showMoreCards}"
            >
              ${dataViewI18nT('kanban.showMoreCards', { count: hiddenCount })}
            </div>`
          : nothing}
        ${this.view.readonly$.value
          ? nothing
          : html`<div class="add-card" @click="${this.clickAddCard}">
              <div
                style="margin-right: 4px;width: 16px;height: 16px;display:flex;align-items:center;"
              >
                ${AddCursorIcon()}
              </div>
              Add
            </div>`}
      </div>
    `;
  }

  @property({ attribute: false })
  accessor group!: Group;

  @property({ attribute: false })
  accessor headerOnly = false;

  @property({ attribute: false })
  accessor hideHeader = false;

  @property({ attribute: false })
  accessor rowKey!: string;

  @property({ attribute: false })
  accessor kanbanViewLogic!: KanbanViewUILogic;

  get view() {
    return this.kanbanViewLogic.view;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-data-view-kanban-group': KanbanGroup;
  }
}
